import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, LogIn, Smartphone, Apple, Instagram, QrCode, Download, Fingerprint } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { isWebAuthnAvailable, loginWithFingerprint, rememberedFingerprintUser } from "@/lib/webauthn";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// QR Code component using external API
const QRCode = ({ url, size = 150 }) => {
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(url)}&bgcolor=141415&color=ffffff`;
  return (
    <img 
      src={qrUrl} 
      alt="QR Code" 
      className="rounded-lg"
      style={{ width: size, height: size }}
    />
  );
};

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState(rememberedFingerprintUser());
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fpLoading, setFpLoading] = useState(false);
  const [showInstall, setShowInstall] = useState(false);
  const fpSupported = isWebAuthnAvailable();
  const fpUser = rememberedFingerprintUser();

  const appUrl = window.location.origin;
  const instagramUrl = "https://instagram.com/livewifi.es"; // Configurable

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error("Ingresa usuario y contraseña");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      
      const { token, user } = response.data;
      localStorage.setItem("token", token);
      localStorage.setItem("user", JSON.stringify(user));
      
      toast.success(`Bienvenido, ${user.username}`);
      onLogin(user);
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center p-4 gap-6">
      {/* Login Card */}
      <Card className="w-full max-w-md bg-[#141415] border-zinc-800">
        <CardHeader className="text-center pb-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_flood-alert-hub/artifacts/3iap2r3z_LOGO%20FINAL%20LIVEWIFI%20blanco%202.png" 
            alt="LiveWifi" 
            style={{ maxHeight: '48px', width: 'auto', height: 'auto' }}
            className="mx-auto mb-4"
          />
          <CardTitle className="font-heading text-2xl uppercase tracking-widest text-zinc-100">
            CENTINELA
          </CardTitle>
          <p className="text-zinc-500 text-sm mt-1">Sistema de Monitoreo de Presas</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-zinc-400 text-xs uppercase tracking-wider">
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingresa tu usuario"
                className="bg-zinc-900 border-zinc-700 text-zinc-100"
                data-testid="login-username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-400 text-xs uppercase tracking-wider">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingresa tu contraseña"
                className="bg-zinc-900 border-zinc-700 text-zinc-100"
                data-testid="login-password"
              />
            </div>
            
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white h-11"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <LogIn className="w-4 h-4 mr-2" />
              )}
              Iniciar Sesión
            </Button>
          </form>

          {/* Download App Section */}
          <div className="mt-6 pt-6 border-t border-zinc-800">
            <Button
              variant="outline"
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              onClick={() => setShowInstall(!showInstall)}
            >
              <Download className="w-4 h-4 mr-2" />
              Descargar App
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Install App Modal/Card */}
      {showInstall && (
        <Card className="w-full max-w-md bg-[#141415] border-zinc-800 animate-in fade-in slide-in-from-bottom-4">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-lg uppercase tracking-wider text-zinc-100 flex items-center justify-center gap-2">
              <QrCode className="w-5 h-5 text-blue-400" />
              Instalar Aplicación
            </CardTitle>
            <p className="text-zinc-500 text-sm mt-1">Escanea el QR o sigue las instrucciones</p>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* QR Code */}
            <div className="flex justify-center">
              <div className="bg-zinc-900 p-4 rounded-xl border border-zinc-700">
                <QRCode url={appUrl} size={180} />
              </div>
            </div>

            {/* Install Instructions */}
            <div className="space-y-3">
              {/* Android */}
              <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-zinc-100 font-semibold">Android</h4>
                    <p className="text-zinc-500 text-xs">Chrome / Samsung Internet</p>
                  </div>
                </div>
                <ol className="text-sm text-zinc-400 space-y-1 ml-13">
                  <li>1. Abre el menú (⋮) del navegador</li>
                  <li>2. Pulsa <span className="text-blue-400">"Añadir a pantalla de inicio"</span></li>
                  <li>3. Confirma la instalación</li>
                </ol>
              </div>

              {/* iOS */}
              <div className="bg-zinc-900/50 rounded-lg p-4 border border-zinc-800">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-zinc-500/20 flex items-center justify-center">
                    <Apple className="w-5 h-5 text-zinc-300" />
                  </div>
                  <div>
                    <h4 className="text-zinc-100 font-semibold">iPhone / iPad</h4>
                    <p className="text-zinc-500 text-xs">Safari</p>
                  </div>
                </div>
                <ol className="text-sm text-zinc-400 space-y-1 ml-13">
                  <li>1. Pulsa el botón compartir (⬆)</li>
                  <li>2. Selecciona <span className="text-blue-400">"Añadir a pantalla de inicio"</span></li>
                  <li>3. Confirma con "Añadir"</li>
                </ol>
              </div>
            </div>

            {/* Close button */}
            <Button
              variant="ghost"
              className="w-full text-zinc-500 hover:text-zinc-300"
              onClick={() => setShowInstall(false)}
            >
              Cerrar
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Social Links */}
      <div className="flex items-center gap-6">
        <a 
          href="https://t.me/Livewifi_bot"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-zinc-500 hover:text-blue-400 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
          </svg>
          <span className="text-sm">@Livewifi_bot</span>
        </a>
        <a 
          href={instagramUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-zinc-500 hover:text-pink-400 transition-colors"
        >
          <Instagram className="w-5 h-5" />
          <span className="text-sm">@livewifi.es</span>
        </a>
      </div>

      {/* Footer */}
      <p className="text-zinc-600 text-xs">
        © 2026 LiveWifi - Todos los derechos reservados
      </p>
    </div>
  );
};

export default Login;
